// Keypair / device setup guided workflow (Spec §8.7 / Figure 24, Table 10).
// A 4-step guided path — Generate → Backup → Verify → Ready — that stands up an
// end-to-end (E2E) Ed25519 signing key for progressive disclosure. Every step
// runs the REAL crypto/API machinery already used by KeypairSetupBanner and
// ManageDevicesPanel — nothing here fakes success (P7 Honesty):
//   • Generate  → services/disclosureKeys.generateKeypair (non-extractable
//                 Ed25519 keypair; the private key never leaves this device) +
//                 storeKeypair (IndexedDB).
//   • Backup    → shows the REAL public-key fingerprint as the durable device
//                 reference. The private key is non-extractable by design, so
//                 there is no exportable seed phrase to write down (see the
//                 honesty note in Step 2).
//   • Verify    → registerDisclosureKeypair (POST /disclosure/keypair) +
//                 getDisclosureKeypair (server confirmation) + a REAL on-device
//                 test-sign round-trip (signPayload → crypto.subtle.verify
//                 against the registered public key). Success reflects the real
//                 server + crypto result; failure shows a retryable message.
//   • Ready     → success state + NextStepBanner and a back-link to matches.
//
// The real functions are injectable via the optional `deps` prop so the flow
// can be exercised end-to-end in tests without module mocking (which the repo
// bans). In the app the route renders <KeypairSetup /> with no props, so the
// real implementations are always used.

import { useEffect, useMemo, useState } from 'react'
import { Link, useLocation, useSearchParams } from 'react-router-dom'
import {
  KeyRound,
  Fingerprint,
  ShieldCheck,
  CheckCircle2,
  AlertTriangle,
  Copy,
  Check,
  ArrowLeft,
  Loader2,
} from 'lucide-react'
import PageHeader from '../../components/ui/PageHeader'
import NextStepBanner, { NextStepArrow } from '../../components/ui/NextStepBanner'
import WorkflowStepper, { type WorkflowStep } from '../../components/ui/WorkflowStepper'
import { FieldHint, ValidationMessage } from '../../components/ui/FieldHint'
import HelpPopover from '../../components/help/HelpPopover'
import { useAuth } from '../../contexts/AuthContext'
import {
  generateKeypair,
  exportPublicKeyHex,
  storeKeypair,
  loadKeypair,
  signPayload,
  isEd25519Supported,
} from '../../services/disclosureKeys'
import { registerDisclosureKeypair, getDisclosureKeypair } from '../../services/api'

// --- small hex helpers (mirror services/disclosureKeys formatting) ----------
function bytesToHex(bytes: Uint8Array): string {
  return Array.from(bytes)
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('')
}

function hexToBytes(hex: string): Uint8Array<ArrayBuffer> {
  const clean = hex.length % 2 ? '0' + hex : hex
  const out = new Uint8Array(clean.length / 2)
  for (let i = 0; i < out.length; i++) out[i] = parseInt(clean.slice(i * 2, i * 2 + 2), 16)
  return out
}

// Real verification: re-import the registered public-key hex with the 'verify'
// usage (the generated public key only carries 'sign' usages) and check the
// signature the on-device private key produced over a random challenge.
async function verifyWithPublicHex(publicKeyHex: string, sigHex: string, msgHex: string): Promise<boolean> {
  const key = await crypto.subtle.importKey('raw', hexToBytes(publicKeyHex), { name: 'Ed25519' }, false, ['verify'])
  return crypto.subtle.verify('Ed25519', key, hexToBytes(sigHex), hexToBytes(msgHex))
}

// --- injectable dependency seam (real implementations by default) -----------
export interface KeypairSetupDeps {
  isEd25519Supported: () => Promise<boolean>
  generateKeypair: () => Promise<CryptoKeyPair>
  exportPublicKeyHex: (publicKey: CryptoKey) => Promise<string>
  storeKeypair: (keypair: CryptoKeyPair, orgId: string) => Promise<void>
  loadKeypair: (orgId: string) => Promise<CryptoKeyPair | null>
  signPayload: (privateKey: CryptoKey, payloadBytesHex: string) => Promise<string>
  verifySignature: (publicKeyHex: string, sigHex: string, msgHex: string) => Promise<boolean>
  registerDisclosureKeypair: (
    publicKeyHex: string,
  ) => Promise<{ registered: boolean; public_key_hex: string; registered_at: string }>
  getDisclosureKeypair: () => Promise<{ has_keypair: boolean; public_key_hex?: string; registered_at?: string }>
}

const DEFAULT_DEPS: KeypairSetupDeps = {
  isEd25519Supported,
  generateKeypair,
  exportPublicKeyHex,
  storeKeypair,
  loadKeypair,
  signPayload,
  verifySignature: verifyWithPublicHex,
  registerDisclosureKeypair,
  getDisclosureKeypair,
}

interface KeypairSetupProps {
  /** Test seam — defaults to the real crypto/API implementations. */
  deps?: Partial<KeypairSetupDeps>
}

type StepIndex = 1 | 2 | 3 | 4

interface KeypairState {
  keypair: CryptoKeyPair
  publicKeyHex: string
}

interface VerifyResult {
  registeredAt?: string
  signatureVerified: boolean
}

export default function KeypairSetup({ deps }: KeypairSetupProps) {
  const d = useMemo<KeypairSetupDeps>(() => ({ ...DEFAULT_DEPS, ...deps }), [deps])
  const { user } = useAuth()
  const orgId = user?.active_org_id ?? null

  // Resolve a safe return target from ?returnTo= or location.state.returnTo.
  // Only same-origin app paths are honoured (guards against open-redirect).
  const [searchParams] = useSearchParams()
  const location = useLocation()
  const rawReturn =
    searchParams.get('returnTo') ??
    (location.state as { returnTo?: string } | null)?.returnTo ??
    null
  const returnTo = rawReturn && rawReturn.startsWith('/') && !rawReturn.startsWith('//') ? rawReturn : null
  const backTo = returnTo ?? '/'
  const backLabel = returnTo ? 'Return to matches' : 'Back to dashboard'

  const [supported, setSupported] = useState<boolean | null>(null)
  const [existingHex, setExistingHex] = useState<string | null>(null)
  const [step, setStep] = useState<StepIndex>(1)
  const [keypairState, setKeypairState] = useState<KeypairState | null>(null)
  const [savedConfirmed, setSavedConfirmed] = useState(false)
  const [verifyResult, setVerifyResult] = useState<VerifyResult | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [copied, setCopied] = useState(false)

  const generated = keypairState !== null
  const verified = verifyResult?.signatureVerified === true

  useEffect(() => {
    let cancelled = false
    async function init() {
      let sup = false
      try {
        sup = await d.isEd25519Supported()
      } catch {
        sup = false
      }
      if (cancelled) return
      setSupported(sup)
      if (sup && orgId) {
        try {
          const existing = await d.loadKeypair(orgId)
          if (existing && !cancelled) {
            const hex = await d.exportPublicKeyHex(existing.publicKey)
            if (!cancelled) setExistingHex(hex)
          }
        } catch {
          // No usable local key — the guided flow will generate a fresh one.
        }
      }
    }
    init()
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId])

  async function handleGenerate() {
    if (!orgId || supported === false || busy) return
    setBusy(true)
    setError(null)
    try {
      const keypair = await d.generateKeypair()
      const publicKeyHex = await d.exportPublicKeyHex(keypair.publicKey)
      await d.storeKeypair(keypair, orgId)
      setKeypairState({ keypair, publicKeyHex })
      setSavedConfirmed(false)
      setVerifyResult(null)
      setStep(2)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not generate your keypair on this device. Please try again.')
    } finally {
      setBusy(false)
    }
  }

  function handleContinueBackup() {
    if (!savedConfirmed) return
    setError(null)
    setStep(3)
  }

  async function handleVerify() {
    if (!keypairState || !orgId || busy) return
    setBusy(true)
    setError(null)
    try {
      // 1) Register the public key with the server (real endpoint).
      const reg = await d.registerDisclosureKeypair(keypairState.publicKeyHex)
      // 2) Ask the server to confirm it now holds a key (real endpoint).
      const status = await d.getDisclosureKeypair()
      if (!status.has_keypair) {
        throw new Error('The server did not confirm your key registration. Please try again.')
      }
      // 3) Real on-device test-sign round-trip against the registered public key.
      const challengeHex = bytesToHex(crypto.getRandomValues(new Uint8Array(32)))
      const sigHex = await d.signPayload(keypairState.keypair.privateKey, challengeHex)
      const signatureVerified = await d.verifySignature(keypairState.publicKeyHex, sigHex, challengeHex)
      if (!signatureVerified) {
        throw new Error(
          'Registration succeeded but the on-device signature check failed — this key cannot sign on this device. Regenerate your keypair and try again.',
        )
      }
      setVerifyResult({ registeredAt: reg.registered_at ?? status.registered_at, signatureVerified: true })
      setStep(4)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not register and verify this device. Please try again.')
    } finally {
      setBusy(false)
    }
  }

  async function copyFingerprint() {
    if (!keypairState) return
    try {
      await navigator.clipboard?.writeText(keypairState.publicKeyHex)
      setCopied(true)
      window.setTimeout(() => setCopied(false), 2000)
    } catch {
      // Clipboard unavailable — the fingerprint is on screen to copy manually.
    }
  }

  const steps: WorkflowStep[] = [
    {
      key: 'generate',
      label: 'Generate',
      hint: 'Create your keypair',
      status: generated ? 'completed' : step === 1 ? 'active' : 'pending',
      onSelect: generated ? () => setStep(1) : undefined,
    },
    {
      key: 'backup',
      label: 'Backup',
      hint: 'Save your reference',
      status: savedConfirmed ? 'completed' : !generated ? 'locked' : step === 2 ? 'active' : 'pending',
      lockReason: !generated ? 'Generate your keypair first' : undefined,
      onSelect: savedConfirmed ? () => setStep(2) : undefined,
    },
    {
      key: 'verify',
      label: 'Verify',
      hint: 'Register & test-sign',
      status: verified ? 'completed' : !savedConfirmed ? 'locked' : step === 3 ? 'active' : 'pending',
      lockReason: !savedConfirmed ? 'Confirm your backup first' : undefined,
      onSelect: verified ? () => setStep(3) : undefined,
    },
    {
      key: 'ready',
      label: 'Ready',
      hint: 'All set',
      status: !verified ? 'locked' : 'completed',
      lockReason: !verified ? 'Verify your device first' : undefined,
    },
  ]

  const backLink = (
    <Link
      to={backTo}
      className="inline-flex items-center gap-1.5 rounded-lg px-3 py-2 text-sm font-medium text-iflow-700 hover:bg-iflow-50"
    >
      <ArrowLeft size={15} aria-hidden />
      {backLabel}
    </Link>
  )

  // --- honest gates: no org / unsupported browser --------------------------
  if (orgId === null) {
    return (
      <div>
        <PageHeader
          title="Set up your keypair"
          description="Required to advance disclosure to full sharing."
          secondaryActions={backLink}
        />
        <div className="flex items-start gap-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-4 text-sm text-amber-800">
          <AlertTriangle size={18} className="mt-0.5 shrink-0" aria-hidden />
          <p>
            You need an active organisation before you can set up a signing key. Select or join an organisation, then
            come back to this page.
          </p>
        </div>
      </div>
    )
  }

  const cardClass = 'rounded-2xl border border-[rgba(12,74,110,0.12)] bg-white/70 p-6 shadow-sm'

  return (
    <div>
      <PageHeader
        title="Set up your keypair"
        description="Generate an end-to-end signing key so you can advance disclosure to full sharing."
        status={verified ? { label: 'Ready', tone: 'success' } : { label: `Step ${step} of 4`, tone: 'info' }}
        secondaryActions={backLink}
      />

      <div className="mb-6">
        <WorkflowStepper steps={steps} aria-label="Keypair setup progress" />
      </div>

      {supported === false && (
        <div className="mb-6 flex items-start gap-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-4 text-sm text-amber-800">
          <AlertTriangle size={18} className="mt-0.5 shrink-0" aria-hidden />
          <p>
            Your browser does not support Ed25519 signing keys, so a keypair cannot be created on this device. Please
            upgrade to Chrome 113+, Safari 17+, or Firefox 130+ and reopen this page.
          </p>
        </div>
      )}

      {/* STEP 1 — GENERATE ------------------------------------------------- */}
      {step === 1 && (
        <section className={cardClass} aria-labelledby="step-generate-heading">
          <div className="mb-3 flex items-center gap-2">
            <span className="flex h-9 w-9 items-center justify-center rounded-full bg-iflow-100 text-iflow-700">
              <KeyRound size={18} aria-hidden />
            </span>
            <h2 id="step-generate-heading" className="text-lg font-semibold text-iflow-900">
              Generate your keypair
            </h2>
            <HelpPopover
              topic="keypair-generate"
              title="What is an E2E keypair?"
              body="A keypair is two matching keys. The private key stays on this device and signs your disclosure commitments; the public key is registered so others can verify your signatures. Because the private key never leaves this device, only you can sign as you — that is what makes it end-to-end."
            />
          </div>
          <p className="mb-3 max-w-2xl text-sm text-slate-600">
            We create an Ed25519 signing key in this browser&rsquo;s secure storage. The private half is generated as
            non-extractable and <strong>never leaves this device</strong> — SecureEmbed only ever sees the public half.
            You use it to sign the tamper-evident commitments that let you advance disclosure to full sharing.
          </p>
          <FieldHint>
            Signing happens locally, so keep using the same device (or add another device later from Manage devices).
          </FieldHint>

          {existingHex && (
            <div className="mt-4 rounded-lg border border-iflow-200 bg-iflow-50 px-3 py-2 text-xs text-iflow-800">
              This device already has a signing key (<code className="font-mono">{existingHex.slice(0, 16)}…</code>).
              Generating again rotates to a new key for this device; previously-signed commitments stay verified.
            </div>
          )}

          {error && <ValidationMessage>{error}</ValidationMessage>}

          <div className="mt-5">
            <button
              type="button"
              onClick={handleGenerate}
              disabled={busy || supported === false || supported === null}
              className="inline-flex items-center gap-2 rounded-lg bg-iflow-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-iflow-700 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {busy ? <Loader2 size={16} className="animate-spin" aria-hidden /> : <KeyRound size={16} aria-hidden />}
              {busy ? 'Generating…' : 'Generate my keypair'}
            </button>
            {supported === null && (
              <p className="mt-2 text-xs text-slate-400">Checking this browser&rsquo;s crypto support…</p>
            )}
          </div>
        </section>
      )}

      {/* STEP 2 — BACKUP --------------------------------------------------- */}
      {step === 2 && keypairState && (
        <section className={cardClass} aria-labelledby="step-backup-heading">
          <div className="mb-3 flex items-center gap-2">
            <span className="flex h-9 w-9 items-center justify-center rounded-full bg-iflow-100 text-iflow-700">
              <Fingerprint size={18} aria-hidden />
            </span>
            <h2 id="step-backup-heading" className="text-lg font-semibold text-iflow-900">
              Back up your device reference
            </h2>
            <HelpPopover
              topic="keypair-backup"
              title="Why there is no seed phrase"
              body="Your private key is generated as non-extractable — it physically cannot be exported from this device, so there is no seed phrase to write down. What you record instead is your public-key fingerprint: it lets you recognise and verify this device in Manage devices. If you lose the device, you simply register a new one; everything you signed before stays valid."
            />
          </div>

          <p className="mb-3 max-w-2xl text-sm text-slate-600">
            Your private key can&rsquo;t be exported (that&rsquo;s what keeps it end-to-end), so there is no seed phrase.
            Instead, save your <strong>public-key fingerprint</strong> below — your durable reference for recognising and
            verifying this device later.
          </p>

          <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
            <div className="mb-1 flex items-center justify-between gap-2">
              <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">Public-key fingerprint</span>
              <button
                type="button"
                onClick={copyFingerprint}
                className="inline-flex items-center gap-1 rounded px-2 py-1 text-xs font-medium text-iflow-700 hover:bg-iflow-50"
              >
                {copied ? <Check size={13} aria-hidden /> : <Copy size={13} aria-hidden />}
                {copied ? 'Copied' : 'Copy'}
              </button>
            </div>
            <code className="block break-all font-mono text-xs text-slate-700">{keypairState.publicKeyHex}</code>
          </div>
          <FieldHint>Store this somewhere you control (a password manager works well). It is not secret, but it proves which key is yours.</FieldHint>

          <div className="mt-4 flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-3 text-sm text-amber-800">
            <AlertTriangle size={16} className="mt-0.5 shrink-0" aria-hidden />
            <p>
              If you clear this browser&rsquo;s data or lose this device, this signing key is gone — you&rsquo;ll register
              a fresh device key. Commitments you already signed remain verifiable.
            </p>
          </div>

          <label className="mt-4 flex items-start gap-2 text-sm text-slate-700">
            <input
              type="checkbox"
              checked={savedConfirmed}
              onChange={(e) => setSavedConfirmed(e.target.checked)}
              className="mt-0.5 h-4 w-4 rounded border-slate-300 text-iflow-600 focus:ring-iflow-500"
            />
            <span>I&rsquo;ve saved my recovery reference and understand this key stays on this device.</span>
          </label>

          <div className="mt-5 flex items-center gap-3">
            <button
              type="button"
              onClick={handleContinueBackup}
              disabled={!savedConfirmed}
              className="inline-flex items-center gap-2 rounded-lg bg-iflow-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-iflow-700 disabled:cursor-not-allowed disabled:opacity-50"
            >
              Continue to verify
            </button>
            {!savedConfirmed && (
              <span className="text-xs text-slate-400">Confirm you&rsquo;ve saved your reference to continue.</span>
            )}
          </div>
        </section>
      )}

      {/* STEP 3 — VERIFY --------------------------------------------------- */}
      {step === 3 && keypairState && (
        <section className={cardClass} aria-labelledby="step-verify-heading">
          <div className="mb-3 flex items-center gap-2">
            <span className="flex h-9 w-9 items-center justify-center rounded-full bg-iflow-100 text-iflow-700">
              <ShieldCheck size={18} aria-hidden />
            </span>
            <h2 id="step-verify-heading" className="text-lg font-semibold text-iflow-900">
              Register &amp; verify this device
            </h2>
            <HelpPopover
              topic="keypair-verify"
              title="What verification does"
              body="We register your public key with SecureEmbed, then run a real test-sign on this device: your private key signs a random challenge and we verify that signature against your registered public key. It confirms — cryptographically, not just visually — that this device can produce valid signatures."
            />
          </div>

          <p className="mb-3 max-w-2xl text-sm text-slate-600">
            This registers your public key, then runs a real signature test on this device to prove it can sign. Nothing
            is marked done unless both the server registration and the on-device signature check actually succeed.
          </p>
          <FieldHint>The test signs a random challenge locally — no disclosure content is touched.</FieldHint>

          {error && <ValidationMessage>{error}</ValidationMessage>}

          <div className="mt-5">
            <button
              type="button"
              onClick={handleVerify}
              disabled={busy}
              className="inline-flex items-center gap-2 rounded-lg bg-iflow-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-iflow-700 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {busy ? <Loader2 size={16} className="animate-spin" aria-hidden /> : <ShieldCheck size={16} aria-hidden />}
              {busy ? 'Registering & verifying…' : 'Register & verify this device'}
            </button>
          </div>
        </section>
      )}

      {/* STEP 4 — READY ---------------------------------------------------- */}
      {step === 4 && verified && (
        <section className={cardClass} aria-labelledby="step-ready-heading">
          <div className="mb-3 flex items-center gap-2">
            <span className="flex h-9 w-9 items-center justify-center rounded-full bg-emerald-100 text-emerald-700">
              <CheckCircle2 size={18} aria-hidden />
            </span>
            <h2 id="step-ready-heading" className="text-lg font-semibold text-iflow-900">
              Your keypair is ready
            </h2>
          </div>

          <p className="mb-3 max-w-2xl text-sm text-slate-600">
            This device is registered and its signature was verified. You can now sign commitments and advance disclosure
            to full sharing.
          </p>

          <ValidationMessage tone="success">
            Signature verified on this device
            {verifyResult?.registeredAt ? ` · registered ${new Date(verifyResult.registeredAt).toLocaleString()}` : ''}.
          </ValidationMessage>

          <div className="mt-5">
            <NextStepBanner
              tone="success"
              stage="Disclosure"
              message="Your keypair is ready — you can now advance disclosure to full sharing."
              action={
                <Link
                  to={backTo}
                  className="inline-flex items-center rounded-lg bg-emerald-600 px-3 py-2 text-sm font-semibold text-white hover:bg-emerald-700"
                >
                  <NextStepArrow>{backLabel}</NextStepArrow>
                </Link>
              }
            />
          </div>
        </section>
      )}
    </div>
  )
}
